import { defineStore } from 'pinia'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useAccessStore } from '@/stores/access'
import { useUserStore } from '@/stores/user'
import type Org from '@/types/Org'
import type OrgMember from '@/types/OrgMember'

const isMemberOf = (orgs: Org[], orgId: number | string) => orgs.some(o => o.organization_id === parseInt(String(orgId)))

let resolvePending: Promise<string | null> | null = null

interface OrgState {
  current: any
  currentMembers: OrgMember[]
  currentInvites: any[]
  pendingInvites: any[]
  orgs: Org[]
  // The selected org id, remembered across reloads.
  selectedOrg: string | null
  apikeys: any[]
}

export const useOrgStore = defineStore('org', {
  state: (): OrgState => ({
    current: null,
    currentMembers: [],
    currentInvites: [],
    pendingInvites: [],
    orgs: [],
    selectedOrg: localStorage.getItem('selectedOrgId') || null,
    apikeys: []
  }),

  getters: {
    getSelectedOrg: (state): Org | undefined => {
      return state.orgs.find(o => o.organization_id === parseInt(state.selectedOrg as string))
    },
  },

  actions: {
    setOrgs(orgs: Org[]) {
      this.orgs = orgs
    },

    setSelectedOrg(org: number | string | null | undefined) {
      this.selectedOrg = org === null || org === undefined ? null : org.toString()
      try {
        if (this.selectedOrg) {
          localStorage.setItem('selectedOrgId', this.selectedOrg)
        } else {
          localStorage.removeItem('selectedOrgId')
        }
      } catch (e) {
        // storage unavailable (private mode / quota) — selection still works in-memory
      }
    },

    setCurrent(current: any) {
      if (this.orgs.length === 1) {
        current = this.orgs[0]
      }
      this.current = current
    },

    replaceMember(member: OrgMember) {
      const index = this.currentMembers.findIndex(m => m.organization_member_id === member.organization_member_id)
      if (index !== -1) {
        this.currentMembers = [
          ...this.currentMembers.slice(0, index),
          member,
          ...this.currentMembers.slice(index + 1)
        ]
      }
    },

    async fetchOrgs() {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization`
        })

        this.orgs = response.data

        // Drop a remembered org the member no longer belongs to (different user on this
        // browser, removed from the org) so it can't be restored on the next load.
        if (this.selectedOrg && !isMemberOf(this.orgs, this.selectedOrg)) {
          this.setSelectedOrg(null)
        }
      } catch (e) {
        toast({ title: "Can't fetch orgs", variant: 'destructive' })
      }
    },

    async fetchOrg(orgId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${orgId}`
        })

        this.setCurrent(response.data)
      } catch (e) {
        toast({ title: `Can't fetch org. ID: ${orgId}`, variant: 'destructive' })
      }
    },

    async searchMembers({ orgId, query, projectId }: { orgId: number | string, query: string, projectId?: number | string | null }) {
      const params: Record<string, unknown> = { q: query }
      if (projectId) params.project_id = projectId
      const response = await API({
        method: 'GET',
        url: `${HOST}/organization/${orgId}/members/search`,
        params
      })
      return response.data
    },

    async fetchOrgMembers(orgId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${orgId}/members`
        })

        const data = response.data
        this.currentMembers = Array.isArray(data) ? data : data.organization_members || []
      } catch (e) {
        toast({ title: `Can't fetch org members. ID: ${orgId}`, variant: 'destructive' })
      }
    },

    async fetchOrgInvites(orgId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${orgId}/invites`
        })

        this.currentInvites = response.data
      } catch (e: any) {
        // Silently ignore 403 Forbidden for non-super-admins (they don't have permission to view invites)
        // But show error for super-admins so they know if something is wrong
        const isSuperAdmin = useUserStore().member?.is_super_user
        if (e.response?.status === 403 && !isSuperAdmin) {
          this.currentInvites = []
          return
        }
        toast({ title: `Can't fetch org invites. ID: ${orgId}`, variant: 'destructive' })
      }
    },

    async invite(payload: { organization_id: number | string } & Record<string, any>) {
      const orgId = payload.organization_id

      try {
        // Send first/last name along with fullname for backward compatibility
        const inviteData = {
          email: payload.email,
          fullname: payload.fullname,
          first_name: payload.first_name || null,
          last_name: payload.last_name || null,
          role_id: payload.role_id
        }

        const response = await API({
          method: 'POST',
          url: `${HOST}/organization/${orgId}/invite`,
          data: inviteData
        })

        this.currentInvites.push(response.data)
        toast({ title: `Invite sent to ${payload.email}` })
        return true
      } catch (e: any) {
        if (e.response?.status === 409) {
          toast({ title: 'User already in organization', variant: 'destructive' })
        } else if (e.response?.status === 400) {
          const detail = e.response.data?.detail
          const message = typeof detail === 'string' ? detail : 'Invalid invite data'
          toast({ title: message, variant: 'destructive' })
        } else if (e.response?.status === 422) {
          // Handle validation errors - show generic message
          toast({ title: 'Invalid invite data', variant: 'destructive' })
        } else {
          const detail = e.response?.data?.detail
          const message = typeof detail === 'string' ? detail : 'Failed to create invite'
          toast({ title: `Can't create invite. ${message}`, variant: 'destructive' })
        }
        return false
      }
    },

    async fetchPendingOrgInvites() {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/invites/pending`
        })

        this.pendingInvites = response.data
      } catch (e) {
        toast({ title: "Can't fetch pending org invites", variant: 'destructive' })
      }
    },

    async acceptOrgInvite(inviteId: number) {
      try {
        await API({
          method: 'PUT',
          url: `${HOST}/organization/invites/${inviteId}/accept`
        })

        this.pendingInvites = this.pendingInvites.filter(i => i.id !== inviteId)
        toast({ title: 'Organization invite accepted', duration: 15000 })
      } catch (e) {
        toast({ title: "Can't accept invite", variant: 'destructive' })
      }
    },

    async deleteOrgInvite(payload: { orgId: number | string, inviteId: number | string }) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/organization/${payload.orgId}/invites/${payload.inviteId}`
        })

        this.currentInvites = this.currentInvites.filter(i => i.id !== payload.inviteId)
        toast({ title: 'Invite deleted' })
      } catch (e) {
        toast({ title: "Can't delete invite", variant: 'destructive' })
      }
    },

    async resendOrgInvite(payload: { orgId: number | string, inviteId: number | string }) {
      try {
        await API({
          method: 'POST',
          url: `${HOST}/organization/${payload.orgId}/invites/${payload.inviteId}/resend`
        })

        toast({ title: 'Invite resent' })
      } catch (e) {
        toast({ title: "Can't resend invite", variant: 'destructive' })
      }
    },

    async updateOrg({ orgId, ...fields }: { orgId: number | string } & Record<string, unknown>) {
      await API({
        method: 'PATCH',
        url: `${HOST}/organization/${orgId}`,
        data: fields
      })
    },

    async updateOrgAvatar({ orgId, file }: { orgId: number | string, file: any }) {
      await API({
        headers: {'Content-Type': 'multipart/form-data'},
        method: 'PATCH',
        url: `${HOST}/organization/${orgId}/update-avatar`,
        data: {file}
      })
    },

    async updateMember(payload: { orgId: number | string, orgMemberId: number, displayName?: string, keySettings?: any }) {
      try {
        const response = await API({
          method: 'PUT',
          url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}`,
          data: {
            display_name: payload.displayName,
            key_settings: payload.keySettings
          }
        })
        this.replaceMember(response.data)
        toast({ title: 'Member updated' })
      } catch (e: any) {
        toast({ title: `Can't update member. ${e.response.data?.detail}`, variant: 'destructive' })
      }
    },

    async setMemberRole({ member, roleId }: { member: OrgMember, roleId: number }) {
      try {
        await useAccessStore().assignOrgRole({memberId: member.member_id, roleId})
        const current = this.currentMembers.find(m => m.organization_member_id === member.organization_member_id)
        this.replaceMember({...(current ?? member), role_id: roleId} as OrgMember)
        toast({ title: 'Role changed' })
        return true
      } catch (e) {
        toast({ title: "Couldn't change the role. Reload and try again.", variant: 'destructive' })
        return false
      }
    },

    async deleteMember(payload: { orgId: number | string, orgMemberId: number }) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}`
        })
        this.currentMembers = this.currentMembers.filter(m => m.organization_member_id !== payload.orgMemberId)
        toast({ title: 'Member deleted' })
      } catch (e: any) {
        toast({ title: `Can't delete member. ${e.response.data?.detail}`, variant: 'destructive' })
      }
    },

    async fetchApikeys(orgId: number | string) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/organization/${orgId}/apikeys`
        })

        this.apikeys = response.data
      } catch (e) {
        toast({ title: `Can't get apikeys`, variant: 'destructive' })
      }
    },

    async removeApiKey(payload: { organization_id: number | string, uuid: string }) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/organization/${payload.organization_id}/apikeys/${payload.uuid}`
        })

        this.apikeys = this.apikeys.filter(a => a.uuid !== payload.uuid)
      } catch (e) {
        toast({ title: `Can't delete apikey`, variant: 'destructive' })
      }
    },

    async addApiKey(payload: { orgId: number | string, name: string, apikey: string }) {
      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/organization/${payload.orgId}/apikeys`,
          data: {
            organization_id: payload.orgId,
            name: payload.name,
            key: payload.apikey
          }
        })

        this.apikeys.push(response.data)
      } catch (e) {
        console.log('error', e)
      }
    },

    async setApiKeyAccess(payload: { orgId: number | string, orgMemberId: number }) {
      try {
        await API({
          method: 'POST',
          url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}/apikey`
        })

        toast({ title: 'Access updated' })
      } catch (e) {
        console.log(e)
      }
    },

    async updateApiKeySettings(payload: { orgId: number | string, orgMemberId: number, keySettings: any }) {
      try {
        await API({
          method: 'PUT',
          url: `${HOST}/organization/${payload.orgId}/members/${payload.orgMemberId}/apikey`,
          data: {
            settings: payload.keySettings
          }
        })
        toast({ title: 'Access updated' })
      } catch (e) {
        console.log(e)
      }
    },

    // Returns the org id to use — the remembered one when it's still valid, otherwise
    // the member's first org. Concurrent callers share one in-flight resolution so they
    // can't each trigger fetchOrgs.
    async resolveSelectedOrg(): Promise<string | null> {
      if (resolvePending) return resolvePending

      const resolve = async () => {
        if (!this.orgs.length) {
          await this.fetchOrgs()
        }
        if (!this.orgs.length) return null

        if (this.selectedOrg && isMemberOf(this.orgs, this.selectedOrg)) {
          return this.selectedOrg
        }

        const fallbackOrgId = this.orgs[0].organization_id.toString()
        this.setSelectedOrg(fallbackOrgId)
        return fallbackOrgId
      }

      resolvePending = resolve().catch(() => {
        toast({ title: 'Failed to initialize selected organization', variant: 'destructive' })
        return null
      })

      try {
        return await resolvePending
      } finally {
        resolvePending = null
      }
    },
  },
})
